import React, { useState, useEffect, useMemo } from "react";
import { MapContainer, TileLayer, Circle, Rectangle, useMapEvents, Popup, Tooltip, useMap } from "react-leaflet";
import "leaflet/dist/leaflet.css";
import colors from '../../../styles/colors.scss'

const getSize = (zoom) => {
	if (zoom > 20) return 20;    // Extremely close (very small circles)
	if (zoom > 18) return 50;    // Close-up view
	if (zoom > 15) return 300;   // Closer view
	if (zoom > 12) return 1000;  // City-level zoom
	if (zoom > 9) return 5000;   // Regional zoom
	if (zoom > 6) return 20000;  // Country-level zoom
	return 200000;               // Far zoom (default)
};

const getColor = (risk) => {
	if (risk) {
		if (risk >= 6.7) return colors.highRisk;
		else if (risk < 6.7 && risk > 3.3) return colors.mediumRisk;
		else if (risk < 3.3) return colors.lowRisk;
	}
	return colors.pepsicoSite
};

// const locations = [
// 	{ id: 10509295, name: "INPROFORM - IND MH PUNE EON IT ZONE", latitude: 18.5515387, longitude: 73.9511152, risk: 7, isPepsico: 0 },
// 	{ id: 14321953, name: "DTDCE - IND MH PUNE 403, LISA STREET", latitude: 18.5204303, longitude: 73.8567437, risk: 10, isPepsico: 0 },
// 	{ id: 14497195, name: "INPROFORM - IND MH MUMBAI", latitude: 19.007922, longitude: 72.835915, risk: 6, isPepsico: 1 },
// 	{ id: 22734134, name: "INPROFORM - CHN BEIJING SHANGHAI", latitude: 31.230416, longitude: 121.473701, risk: 7, isPepsico: 1 },
// 	{ id: 25228887, name: "DTDCE - IND MAHARASHTRA PUNE", latitude: 18.5575988, longitude: 73.8336623, risk: 4, isPepsico: 0 },
// 	{ id: 28611190, name: "STAN METALS - USA MN EDEN PRAIRIE", latitude: 44.8918991, longitude: -93.3413849, risk: 9, isPepsico: 0 },
// 	{ id: 28611191, name: "STAN METALS - USA CA SAN JOSE", latitude: 37.3830631, longitude: -121.9339939, risk: 5, isPepsico: 1 },
// 	{ id: 28611192, name: "STAN METALS - KOR SEOUL SEONGDONG", latitude: 37.5633415, longitude: 127.0371025, risk: 5, isPepsico: 0 },
// 	{ id: 28611193, name: "MOTOE5 - USA IL ELMHURST", latitude: 41.9252391, longitude: -87.9462844, risk: 7, isPepsico: 1 },
// 	{ id: 28611194, name: "MOTOE5 - KOR SEOUL SEONGDONG", latitude: 37.5633415, longitude: 127.0371025, risk: 3, isPepsico: 0 }
// ];

const ResetButton = ({ center, zoom }) => {
	const map = useMap(); // Access the map instance

	const resetMap = () => {
		map.setView(center, zoom); // Reset view to initial center and zoom
	};

	return (
		<button
			onClick={resetMap}
			style={{
				position: "absolute",
				top: "10px",
				right: "10px",
				zIndex: 1000,
				background: "#fff",
				padding: "8px 12px",
				border: "1px solid #ccc",
				borderRadius: "5px",
				cursor: "pointer",
				fontWeight: "bold"
			}}
		>
			Reset
		</button>
	);
};

// Custom Hook to Track Zoom Level
const ZoomListener = ({ setZoomLevel }) => {
	useMapEvents({
		zoomend: (e) => setZoomLevel(e.target.getZoom()),
	});
	return null;
};

const ScrollZoomToggle = ({ allowZoom }) => {
	const map = useMap();

	useEffect(() => {
		if (allowZoom) {
			map.scrollWheelZoom.enable();
		} else {
			map.scrollWheelZoom.disable();
		}
	}, [allowZoom, map]);

	return null;
};


const MapWithShapes = ({ locations, allowMapZoom }) => {
	const [zoomLevel, setZoomLevel] = useState(2);
	const center = [0, 0];

	const shapes = useMemo(() => {
		const radius = getSize(zoomLevel);
		const squareSize = radius * 0.5 / 90000;

		return locations.map((loc) => {
			if (loc.isPepsico === "1") {
				// Render Blue Square
				return (
					<Rectangle
						key={loc.id}
						bounds={[
							[loc.latitude - squareSize / 2, loc.longitude - squareSize / 2],
							[loc.latitude + squareSize / 2, loc.longitude + squareSize / 2]
						]}
						color={colors.pepsicoSite} //#0069D7
						fillColor={colors.pepsicoSite}
						fillOpacity={1.0}
					>
						{/* <Popup>{loc.name}</Popup> */}
						<Tooltip direction="top" offset={[0, -10]} opacity={1} permanent={false}>
							{loc.name}

						</Tooltip>
					</Rectangle>
				);
			} else {
				// Render Red Circle
				return (
					<Circle
						key={loc.id}
						center={[loc.latitude, loc.longitude]}
						radius={radius} // Keep consistent size
						color="transparent"
						fillColor={getColor(loc.risk)}
						fillOpacity={0.5}
					>
						{/* <Popup>{loc.name}</Popup> */}
						<Tooltip direction="top" offset={[0, -10]} opacity={1} permanent={false}>
							{`${loc.name}:${loc.risk}`}
						</Tooltip>
					</Circle>
				);
			}
		});
	}, [locations, zoomLevel]);

	return (
		<div style={{ width: "100%", height: "600px", position: "relative" }}>
			<MapContainer
				center={[0, 0]}
				zoom={3}
				minZoom={1} // Prevent zooming out beyond level 2
				maxBounds={[[-90, -180], [90, 180]]} // Keeps the map within the world bounds
				style={{ height: "100%", width: "100%", backgroundColor: "#fbfcfe" }}
				scrollWheelZoom={allowMapZoom}
				preferCanvas={true}
			>
				<TileLayer
					url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
					noWrap={true} // Prevents horizontal map repetition
				/>
				<ZoomListener setZoomLevel={setZoomLevel} />
				<ScrollZoomToggle allowZoom={allowMapZoom} />

				{shapes}
				{/* <ResetButton center={center} zoom={zoomLevel} /> */}
			</MapContainer>
		</div>
	);
};

export default React.memo(MapWithShapes);
